import React, { ReactNode, useState } from "react";
import { BillableElement } from "../../../../../reducers/uploadedBoard";
import { EditButton } from "../ElementsTableStyles";
import Icon from "../../../../common/Icon";
import FlashPCBTooltip from "../../../../common/FlashPCBTooltip";
import Placement from "../common/Placement";
import ModalContentWithCloseButton from "../../../../common/ModalContentWithCloseButton";
import UpdateElementPlacement from "../../SelectPart/UpdateElementPlacement";
import Modal from "../../../../common/Modal";
import { TableCell } from "../../../../../constants/Styles";
import { useAppSelector } from "../../../../../utils/hooks/store";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../../../utils/group";
import {
  elementCannotCheckPlacement,
  elementHasGoodPlacement,
  elementHasMissingFootprintInfo,
  elementHasMissingPlacementInfo,
  elementHasPlacementError,
  elementHasPlacementWarning,
  elementShouldCheckPlacement,
} from "../../../../../utils/placementChecks";

const PLACEMENT_CHECK_IN_CELL_IS_DISABLED = true;

export function PlacementCheckSummary({
  element,
}: {
  element: BillableElement;
}) {
  const { groups } = useAppSelector((state) => state.auth);
  const userIsAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);

  if (elementShouldCheckPlacement(element)) {
    return (
      <FlashPCBTooltip
        content={
          <div>
            This part has been updated since we last ran placement checks
            against this board. Please rerun the checks via the button above.
          </div>
        }
      >
        <Icon src="/icons/alert_yellow.svg" alt="alert" />
      </FlashPCBTooltip>
    );
  }

  if (elementHasMissingPlacementInfo(element)) {
    return (
      <FlashPCBTooltip
        content={
          <div>
            We do not have placement information for this component. An
            engineering fee will be incurred for any board with missing
            placement information.
          </div>
        }
      >
        <Icon src="/icons/alert_yellow.svg" alt="alert" />
      </FlashPCBTooltip>
    );
  }

  if (elementCannotCheckPlacement(element) && userIsAdminOrEngineer) {
    return (
      <FlashPCBTooltip
        content={<div>We are unable to check the placement for this part.</div>}
      >
        <Icon src="/icons/alert_yellow.svg" alt="alert" />
      </FlashPCBTooltip>
    );
  }

  if (elementHasPlacementError(element)) {
    return (
      <FlashPCBTooltip
        content={
          <>
            <div>
              Our checks determined that the part selected may not match the
              footprint for this element or the placement info for this element
              is incorrect.
            </div>
            <div>
              Match score:{" "}
              {(
                element.part_placement_check.match_score as number
              ).toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}
            </div>
            <div>
              Threshold:{" "}
              {(
                element.part_placement_check.match_threshold as number
              ).toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}
            </div>
          </>
        }
      >
        <Icon src="/icons/alert_red.svg" alt="alert" />
      </FlashPCBTooltip>
    );
  }
  if (elementHasMissingFootprintInfo(element) && userIsAdminOrEngineer) {
    return (
      <FlashPCBTooltip
        content={
          <div>
            We are unable to check the placement of this part because we cannot
            find a footprint for this part. To ensure manufacturability, please
            check that the part selected matches the footprint for this element.
          </div>
        }
      >
        <Icon src="/icons/alert_yellow.svg" alt="alert" />
      </FlashPCBTooltip>
    );
  }
  if (elementHasGoodPlacement(element)) {
    return (
      <FlashPCBTooltip
        content={
          <>
            <div>
              Our checks determined that the part selected is acceptable for
              this element.
            </div>
            <div>
              Match score:{" "}
              {(
                element.part_placement_check.match_score as number
              ).toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}
            </div>
            <div>
              Threshold:{" "}
              {(
                element.part_placement_check.match_threshold as number
              ).toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}
            </div>
          </>
        }
      >
        <Icon src="/icons/check_green.svg" alt="check" />
      </FlashPCBTooltip>
    );
  }
  return null;
}

export default function PlacementCells({
  element,
  elementsInGroup,
  disableEditing,
  groupIsExpanded,
  expandRowToggle,
}: {
  element: BillableElement;
  elementsInGroup: BillableElement[];
  disableEditing: boolean;
  groupIsExpanded: boolean;
  expandRowToggle: ReactNode;
}) {
  const [updatePlacementFormOpen, setUpdatePlacementFormOpen] =
    useState<boolean>(false);
  const [elementToUpdate, setElementToUpdate] =
    useState<BillableElement | null>(null);
  const { groups } = useAppSelector((state) => state.auth);
  const userIsAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);

  const updatePlacement = (element: BillableElement) => {
    setUpdatePlacementFormOpen(true);
    setElementToUpdate(element);
  };

  const closeModal = () => {
    setUpdatePlacementFormOpen(false);
    setElementToUpdate(null);
  };

  const getPlacementContent = (element: BillableElement, isGroup: boolean) => {
    if (isGroup && !groupIsExpanded) {
      return <div />;
    }

    if (element.placement === null) {
      return <div />;
    }

    return <Placement placement={element.placement} />;
  };

  const getPlacementCheck = (element: BillableElement, isGroup: boolean) => {
    if (PLACEMENT_CHECK_IN_CELL_IS_DISABLED) {
      return null;
    }
    if (isGroup && !groupIsExpanded) {
      const placementErrorInGroup = elementsInGroup.some((el) => {
        return elementHasPlacementError(el);
      });
      const placementWarningInGroup = elementsInGroup.some((el) =>
        elementHasPlacementWarning(userIsAdminOrEngineer, el),
      );
      const allPlacementsInGroupAreGood = elementsInGroup.every((el) => {
        return elementHasGoodPlacement(el);
      });
      if (placementWarningInGroup) {
        return (
          <FlashPCBTooltip
            content={
              <div>
                There are elements in this group whose part placement may
                require attention. Expand this group to see more.
              </div>
            }
          >
            <Icon src="/icons/alert_yellow.svg" alt="alert" />
          </FlashPCBTooltip>
        );
      }
      if (placementErrorInGroup) {
        return (
          <FlashPCBTooltip
            content={
              <div>
                An element in this group has a placement error. Expand this
                group to see more.
              </div>
            }
          >
            <Icon src="/icons/alert_red.svg" alt="alert" />
          </FlashPCBTooltip>
        );
      }
      if (allPlacementsInGroupAreGood) {
        return (
          <FlashPCBTooltip
            content={
              <div>
                All part placements in this group have been determined to be
                acceptable for these elements.
              </div>
            }
          >
            <Icon src="/icons/check_green.svg" alt="check" />
          </FlashPCBTooltip>
        );
      }
    }
    return <PlacementCheckSummary element={element} />;
  };

  const getEditPlacementContent = (
    element: BillableElement,
    isGroup: boolean,
  ) => {
    if (isGroup && !groupIsExpanded) {
      return expandRowToggle;
    }
    return (
      <EditButton
        onClick={disableEditing ? undefined : () => updatePlacement(element)}
        disabled={disableEditing}
      >
        <Icon src="/icons/pencil.svg" alt="edit" />
      </EditButton>
    );
  };

  return (
    <>
      <Modal isOpen={updatePlacementFormOpen} onRequestClose={closeModal}>
        <ModalContentWithCloseButton
          closeModal={closeModal}
          showCloseHint
          fullScreen
          lessPadding
          title={`Update placement info for ${elementToUpdate?.name}`}
        >
          {elementToUpdate !== null && (
            <UpdateElementPlacement
              selectedElement={elementToUpdate}
              onSubmit={closeModal}
              onCancel={closeModal}
            />
          )}
        </ModalContentWithCloseButton>
      </Modal>
      <TableCell>
        {getPlacementContent(element, elementsInGroup.length > 1)}
      </TableCell>
      <TableCell>
        {getPlacementCheck(element, elementsInGroup.length > 1)}
      </TableCell>
      <TableCell borderRight>
        {getEditPlacementContent(element, elementsInGroup.length > 1)}
      </TableCell>
    </>
  );
}
